import { getBorgoDellaSettimana, placesMap } from "../api";
import { GEO_FALLBACK, getPositionWithFallback } from "./getPositionWithFallback";



const STORAGE_KEY = "6amico_last_comune";

const STORAGE_NAME_KEY = "6amico_last_comune_name";

/** Attesa massima popup permesso GPS — poi fallback. */
const GEO_TIMEOUT_MS = 3000;

const BORGO_FETCH_MS = 5000;

/** Cap totale risoluzione geo nel contesto ricerca (allineato al popup). */
const CONTEXT_GEO_CAP_MS = 3000;

/** Posizione di default — allineata al fallback geolocalizzazione (Campli). */
export const DEFAULT_COMUNE = {
  slug: GEO_FALLBACK.slug,
  name: GEO_FALLBACK.name,
  lat: GEO_FALLBACK.lat,
  lng: GEO_FALLBACK.lng,
  source: "default",
};

export function getDefaultSearchContext() {
  return { ...DEFAULT_COMUNE };
}



let borgoCache = null;

let borgoPromise = null;

let contextPromise = null;

let contextPromiseKey = "";



export function comuneSlugFromPath(pathname = "") {

  const m = pathname.match(/^\/comune\/([^/]+)/);

  return m ? decodeURIComponent(m[1]) : null;

}



export function saveLastComune(slug, name) {

  if (!slug || typeof localStorage === "undefined") return;

  try {

    localStorage.setItem(STORAGE_KEY, slug);

    if (name) localStorage.setItem(STORAGE_NAME_KEY, name);

  } catch {

    /* ignore quota */

  }

}



export function getLastComuneFromStorage() {

  if (typeof localStorage === "undefined") return null;

  try {

    const slug = localStorage.getItem(STORAGE_KEY);

    if (!slug) return null;

    return {

      slug,

      name: localStorage.getItem(STORAGE_NAME_KEY) || slug,

      source: "storage",

    };

  } catch {

    return null;

  }

}



function withTimeout(promise, ms, fallback = null) {

  return Promise.race([

    promise,

    new Promise((resolve) => {

      setTimeout(() => resolve(fallback), ms);

    }),

  ]);

}



async function loadBorgoDefault() {

  if (borgoCache) return borgoCache;

  if (!borgoPromise) {

    borgoPromise = withTimeout(

      getBorgoDellaSettimana()

        .then((data) => {

          const c = data?.comune;

          if (!c?.slug) return null;

          borgoCache = { slug: c.slug, name: c.name || c.slug, source: "borgo" };

          return borgoCache;

        })

        .catch(() => null),

      BORGO_FETCH_MS,

    ).finally(() => {

      borgoPromise = null;

    });

  }

  return borgoPromise;

}



function nearestComuneFromMapItems(items, lat, lng) {

  let best = null;

  let bestD = Infinity;

  for (const it of items || []) {

    if (it.kind !== "comune" || it.lat == null || it.lng == null) continue;

    const d = (it.lat - lat) ** 2 + (it.lng - lng) ** 2;

    if (d < bestD) {

      bestD = d;

      best = it;

    }

  }

  return best;

}



async function geolocationPermissionState() {

  if (typeof navigator === "undefined" || !navigator.permissions?.query) {

    return "unknown";

  }

  try {

    const perm = await withTimeout(
      navigator.permissions.query({ name: "geolocation" }),
      800,
      null,
    );

    return perm?.state || "unknown";

  } catch {

    return "unknown";

  }

}



export async function tryGeolocationComune() {
  try {
    const pos = await getPositionWithFallback({
      timeout: GEO_TIMEOUT_MS,
      maximumAge: 300000,
      enableHighAccuracy: false,
    });

    // Deny / timeout / unsupported: non bloccare — null → caller usa storage/borgo/default
    if (pos.usedFallback) {
      return null;
    }

    const { lat, lng } = pos;
    const pad = 0.12;

    try {
      const data = await withTimeout(
        placesMap({
          min_lat: lat - pad,
          min_lng: lng - pad,
          max_lat: lat + pad,
          max_lng: lng + pad,
          kinds: "comune",
          limit: 40,
        }),
        GEO_TIMEOUT_MS,
        null,
      );

      const hit = nearestComuneFromMapItems(data?.items, lat, lng);
      if (hit?.slug) {
        return { slug: hit.slug, name: hit.name || hit.slug, source: "geo", lat, lng };
      }
    } catch {
      /* fall through */
    }

    return null;
  } catch {
    return null;
  }
}



async function resolveSearchContextInner(pathname) {

  const fromPath = comuneSlugFromPath(pathname);

  if (fromPath) {

    const ctx = { slug: fromPath, name: fromPath, source: "url" };

    saveLastComune(fromPath);

    return ctx;

  }



  const stored = getLastComuneFromStorage();

  if (stored) return stored;



  const geo = await withTimeout(tryGeolocationComune(), CONTEXT_GEO_CAP_MS, null);

  if (geo) {

    saveLastComune(geo.slug, geo.name);

    return geo;

  }



  const borgo = await loadBorgoDefault();

  if (borgo) return borgo;



  return getDefaultSearchContext();

}



/**

 * Resolve territorial context: URL → localStorage → geolocation → Borgo della settimana.

 * Deduplicated so multiple search bars on the same page share one inflight resolution.

 */

export async function resolveSearchContext(pathname = "") {

  const key = pathname || "";

  const fromPath = comuneSlugFromPath(key);

  if (fromPath) {

    const ctx = { slug: fromPath, name: fromPath, source: "url" };

    saveLastComune(fromPath);

    return ctx;

  }



  const stored = getLastComuneFromStorage();

  if (stored) return stored;



  if (contextPromise && contextPromiseKey === key) {

    return contextPromise;

  }



  contextPromiseKey = key;

  contextPromise = resolveSearchContextInner(key).finally(() => {

    contextPromise = null;

    contextPromiseKey = "";

  });

  return contextPromise;

}



export function prioritizeByContext(items, contextSlug, slugKey = "slug") {

  if (!contextSlug || !items?.length) return items || [];

  const slug = contextSlug.toLowerCase();

  return [...items].sort((a, b) => {

    const aMatch = (a[slugKey] || "").toLowerCase() === slug ? 0 : 1;

    const bMatch = (b[slugKey] || "").toLowerCase() === slug ? 0 : 1;

    if (aMatch !== bMatch) return aMatch - bMatch;

    const aComune = (a.comune_slug || "").toLowerCase() === slug ? 0 : 1;

    const bComune = (b.comune_slug || "").toLowerCase() === slug ? 0 : 1;

    return aComune - bComune;

  });

}


